import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Ban, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiError } from '../../lib/apiError';

const StudentAccountActions = ({ student, onChanged }) => {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null); // 'block' | 'remove'
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (fn, success) => {
    setBusy(true);
    try {
      const { data } = await fn();
      toast.success(data?.message || success);
      setDialog(null);
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const block = async () => {
    if (await run(() => axios.post(`${API}/staff/students/${student.id}/block`, { reason: reason || null }), 'Student blocked')) {
      setReason('');
      onChanged();
    }
  };
  const unblock = async () => {
    if (await run(() => axios.post(`${API}/staff/students/${student.id}/unblock`), 'Student unblocked')) onChanged();
  };
  const remove = async () => {
    if (await run(() => axios.delete(`${API}/staff/students/${student.id}`), 'Student removed')) navigate('/staff/students');
  };

  return (
    <>
      {student.is_blocked ? (
        <Button variant="outline" size="sm" className="rounded-full border-emerald-300 text-emerald-700" onClick={unblock} disabled={busy} data-testid="unblock-student-button">
          <ShieldCheck className="w-4 h-4 mr-1.5" />Unblock
        </Button>
      ) : (
        <Button variant="outline" size="sm" className="rounded-full border-amber-300 text-amber-700" onClick={() => setDialog('block')} data-testid="block-student-button">
          <Ban className="w-4 h-4 mr-1.5" />Block
        </Button>
      )}
      <Button variant="outline" size="sm" className="rounded-full border-rose-300 text-rose-600" onClick={() => { setConfirmText(''); setDialog('remove'); }} data-testid="remove-student-button">
        <Trash2 className="w-4 h-4 mr-1.5" />Remove
      </Button>

      <AlertDialog open={dialog === 'block'} onOpenChange={(v) => !v && setDialog(null)}>
        <AlertDialogContent data-testid="block-student-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Block {student.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              They'll be signed out right away and can't log in until you unblock them. Their data is kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="block-reason">Reason (only staff see this)</Label>
            <Input id="block-reason" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} data-testid="block-reason-input" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); block(); }} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-block-student">
              Block Student
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={dialog === 'remove'} onOpenChange={(v) => !v && setDialog(null)}>
        <AlertDialogContent data-testid="remove-student-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently remove {student.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the account and everything linked to it — tests, analyses, practice attempts, bookings,
              payments, refunds, tickets and notes. Finance totals will change. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="remove-confirm">Type the student's email to confirm</Label>
            <Input id="remove-confirm" placeholder={student.email} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="remove-confirm-input" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); remove(); }}
              disabled={busy || confirmText.trim().toLowerCase() !== (student.email || '').toLowerCase()}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-remove-student"
            >
              Remove Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default StudentAccountActions;
